import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ClipboardList, Loader2, Plus, Trash2, Save, Users, CalendarDays, FileText, Upload, File as FileIcon, Download, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const docHref = (url) => (url?.startsWith('/api') ? `${BACKEND_URL}${url}` : `${API}${url}`);

// Bugünün tarihi (Türkiye), YYYY-MM-DD
const todayStr = () => new Date().toLocaleDateString('en-CA');

const emptyWorker = () => ({ name: '', role: '', hours: '' });

const ServiceDailyReports = ({ serviceId, canEdit: canEditProp }) => {
  const [reports, setReports] = useState([]);
  const [canEdit, setCanEdit] = useState(!!canEditProp);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [date, setDate] = useState(todayStr());
  const [workers, setWorkers] = useState([emptyWorker()]);
  const [reportText, setReportText] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadReports = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/services/${serviceId}/daily-reports`, { headers: getAuthHeaders() });
      setReports(res.data?.reports || []);
      setCanEdit(res.data?.can_edit ?? !!canEditProp);
    } catch (err) {
      console.error('Günlük raporlar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [serviceId, canEditProp]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Seçili tarihe ait kayıt varsa forma yükle
  useEffect(() => {
    const existing = reports.find((r) => r.date === date);
    if (existing) {
      setWorkers(existing.workers?.length ? existing.workers.map((w) => ({ ...w })) : [emptyWorker()]);
      setReportText(existing.report_text || '');
      setDocuments(existing.documents ? existing.documents.map((d) => ({ ...d })) : []);
    } else {
      setWorkers([emptyWorker()]);
      setReportText('');
      setDocuments([]);
    }
  }, [date, reports]);

  const updateWorker = (idx, field, value) => {
    setWorkers((prev) => prev.map((w, i) => (i === idx ? { ...w, [field]: value } : w)));
  };
  const addWorker = () => setWorkers((prev) => [...prev, emptyWorker()]);
  const removeWorker = (idx) => setWorkers((prev) => (prev.length === 1 ? [emptyWorker()] : prev.filter((_, i) => i !== idx)));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('files', file);
        const res = await axios.post(`${API}/upload/offers`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
        const uploaded = res.data?.files?.[0];
        if (uploaded) {
          setDocuments((prev) => [...prev, { filename: uploaded.filename || file.name, url: uploaded.url }]);
        }
      }
      toast.success('Belge(ler) yüklendi');
    } catch (err) {
      toast.error('Belge yüklenemedi');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const removeDocument = (idx) => setDocuments((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!date) { toast.error('Tarih seçin'); return; }
    const cleanWorkers = workers.filter((w) => (w.name || w.role || w.hours));
    if (cleanWorkers.length === 0 && !reportText.trim() && documents.length === 0) {
      toast.error('En az bir çalışan satırı, rapor metni veya belge girin');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/services/${serviceId}/daily-reports`,
        { date, workers: cleanWorkers, report_text: reportText, documents },
        { headers: getAuthHeaders() }
      );
      toast.success('Günlük kayıt kaydedildi');
      await loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (d) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' }); }
    catch { return d; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="service-daily-reports">
      {/* Günlük Kayıt Formu (yetki varsa) */}
      {canEdit && (
        <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4 space-y-3" data-testid="daily-report-form">
          <div className="flex items-center gap-2 text-amber-800 font-semibold">
            <ClipboardList className="h-4 w-4" />
            Günlük Çalışan Listesi & Günün Raporu
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm text-gray-600 whitespace-nowrap">Tarih</Label>
            <Input
              type="date"
              value={date}
              max={todayStr()}
              onChange={(e) => setDate(e.target.value)}
              className="max-w-[180px] bg-white"
              data-testid="daily-report-date"
            />
            {reports.some((r) => r.date === date) && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">Mevcut kayıt düzenleniyor</Badge>
            )}
          </div>

          {/* Çalışan satırları */}
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1fr_120px_36px] gap-2 text-xs font-medium text-gray-500 px-1">
              <span>Adı Soyadı</span>
              <span>Görevi</span>
              <span>Çalışma Süresi</span>
              <span></span>
            </div>
            {workers.map((w, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_120px_36px] gap-2 items-center">
                <Input value={w.name} onChange={(e) => updateWorker(idx, 'name', e.target.value)} placeholder="Ad Soyad" className="bg-white" data-testid={`worker-name-${idx}`} />
                <Input value={w.role} onChange={(e) => updateWorker(idx, 'role', e.target.value)} placeholder="Görev" className="bg-white" data-testid={`worker-role-${idx}`} />
                <Input value={w.hours} onChange={(e) => updateWorker(idx, 'hours', e.target.value)} placeholder="örn. 8 saat" className="bg-white" data-testid={`worker-hours-${idx}`} />
                <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:text-red-600 h-9 w-9" onClick={() => removeWorker(idx)} data-testid={`worker-remove-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="gap-1 text-amber-700 border-amber-300" onClick={addWorker} data-testid="worker-add-btn">
              <Plus className="h-3 w-3" /> Çalışan Satırı Ekle
            </Button>
          </div>

          <div className="space-y-1">
            <Label className="text-sm text-gray-600">Günün Raporu</Label>
            <Textarea value={reportText} onChange={(e) => setReportText(e.target.value)} rows={3} placeholder="Bugün yapılan işler, durum, notlar..." className="bg-white" data-testid="daily-report-text" />
          </div>

          {/* Belgeler */}
          <div className="space-y-1.5">
            <Label className="text-sm text-gray-600">Belgeler</Label>
            {documents.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {documents.map((doc, di) => (
                  <div key={di} className="flex items-center gap-1.5 bg-white border rounded px-2 py-1 text-xs" data-testid={`daily-doc-form-${di}`}>
                    <FileIcon className="h-3.5 w-3.5 text-amber-600" />
                    <a href={docHref(doc.url)} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline max-w-[160px] truncate">{doc.filename}</a>
                    <button type="button" onClick={() => removeDocument(di)} className="text-red-400 hover:text-red-600" data-testid={`daily-doc-remove-${di}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="inline-flex items-center gap-1.5 text-xs text-amber-700 border border-amber-300 rounded px-2.5 py-1.5 cursor-pointer hover:bg-amber-100 w-fit" data-testid="daily-doc-upload-label">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Belge Ekle
              <input type="file" multiple className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="daily-doc-input" />
            </label>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving || uploading} className="bg-amber-600 hover:bg-amber-700 gap-2" data-testid="daily-report-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Kaydet
            </Button>
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-500 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Bu bölümü yalnızca görüntüleyebilirsiniz (günlük rapor sorumlusu değilsiniz).
        </div>
      )}

      {/* Geçmiş Kayıtlar */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-gray-700 font-semibold text-sm">
          <CalendarDays className="h-4 w-4 text-gray-500" />
          Geçmiş Günlük Kayıtlar ({reports.length})
        </div>
        {reports.length === 0 ? (
          <p className="text-gray-400 text-sm">Henüz günlük kayıt girilmemiş.</p>
        ) : (
          <div className="space-y-3" data-testid="daily-report-history">
            {reports.map((r) => (
              <div key={r.id || r.date} className="border rounded-lg p-3 bg-white" data-testid={`daily-report-item-${r.date}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-amber-700 text-sm flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(r.date)}
                  </span>
                  {r.updated_by_name && (
                    <span className="text-xs text-gray-400">{r.updated_by_name}</span>
                  )}
                </div>
                {r.workers?.length > 0 && (
                  <div className="overflow-x-auto mb-2">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 border-b">
                          <th className="text-left py-1 pr-2 font-medium">Adı Soyadı</th>
                          <th className="text-left py-1 pr-2 font-medium">Görevi</th>
                          <th className="text-left py-1 font-medium">Çalışma Süresi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.workers.map((w, wi) => (
                          <tr key={wi} className="border-b last:border-0">
                            <td className="py-1 pr-2">{w.name || '-'}</td>
                            <td className="py-1 pr-2">{w.role || '-'}</td>
                            <td className="py-1">{w.hours || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {r.report_text && (
                  <div className="text-sm bg-gray-50 rounded p-2">
                    <span className="text-xs text-gray-500 flex items-center gap-1 mb-0.5"><FileText className="h-3 w-3" /> Günün Raporu</span>
                    <p className="text-gray-700 whitespace-pre-wrap">{r.report_text}</p>
                  </div>
                )}
                {r.documents?.length > 0 && (
                  <div className="mt-2 pt-2 border-t" data-testid={`daily-report-docs-${r.date}`}>
                    <span className="text-xs font-medium text-gray-700 flex items-center gap-1 mb-1">
                      <FileIcon className="h-3 w-3 text-amber-600" /> Belgeler ({r.documents.length})
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {r.documents.map((doc, di) => (
                        <a
                          key={di}
                          href={docHref(doc.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 bg-amber-50 rounded px-2 py-1 text-xs text-amber-700 hover:bg-amber-100"
                          data-testid={`daily-report-doc-${r.date}-${di}`}
                        >
                          <Download className="h-3 w-3" />
                          {doc.filename || 'Belge'}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {(!r.workers?.length && !r.report_text && !r.documents?.length) && (
                  <p className="text-gray-400 text-xs">Boş kayıt</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDailyReports;
